import _ from 'lodash'
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { useMediaQuery, useTheme } from '@mui/material'
import Table from '@/components/ui/tables/Table' // Add this import

import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'

import LaunchIcon from '@mui/icons-material/Launch'
import Box from '@mui/material/Box'

import ChipButton from '@/components/ui/buttons/ChipButton'
import CalendarIcon from '@/components/ui/icons/Calendar'
import DoctorIcon from '@/components/ui/icons/Doctor'
import ConsultationsCardView from '@/pages/consultations/components/ConsultationsCardView'
import { redirectTo } from '@/utils/consultations'
import { ConsultationData } from '@/pages/consultations/components/ConsultationsTable'
import { getCoreRowModel, getPaginationRowModel, PaginationState, useReactTable } from '@tanstack/react-table'
import Pagination from '@/components/ui/pagination/Pagination'
import { ColumnDef } from '@tanstack/react-table'
import { ConsultationLightTableMobile } from './ConsultationLightTableMobile'

export type ConsultationLightTableProps = {
  data: ConsultationData[]
  minWidth?: number | string
  isDoctor?: boolean
  onAcceptConsultation?: (consultationId: string) => void
  onRefuseConsultation?: (consultationId: string) => void
  disableActions?: boolean
  defaultPageSize?: number
  pageSizeOptions?: number[]
  totalItems?: number
  fetchConsultations?: (options: {
    patientId: string,
    options: {
      order: 'DESC',
      limit: number,
      skip: number
    }
  }) => void
  patientId?: string
}

export function ConsultationLightTable(props: ConsultationLightTableProps) {
  const {
    data,
    onAcceptConsultation,
    onRefuseConsultation,
    disableActions = false,
    defaultPageSize = 5,
    pageSizeOptions = [5, 10, 25],
    fetchConsultations,
    totalItems,
    patientId
  } = props

  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))


  const columns = useMemo<ColumnDef<ConsultationData>[]>(() => {
    return [
      {
        id: 'doctor',
        header: t('component.ui.consultationLight.header.doctor'),
        accessorFn: (row) => row.doctor,
        cell: ({ row }) => {
          const doctor = row.original.doctor
          return (
            <Stack direction="row" spacing={1} alignItems="center">
              {!!doctor ? (
                <>
                  <DoctorIcon />
                  <span>{_.trim(`${doctor?.name || ''} ${doctor?.surname || ''}`)}</span>
                </>
              ) : (
                <Chip
                  color="secondary"
                  label={<span>{t('component.ui.consultationLight.body.column.doctor.toSend')}</span>}
                  variant="filled"
                />
              )}
            </Stack>
          )
        },
      },
      {
        id: 'startDate',
        header: t('component.ui.consultationLight.header.startDate'),
        accessorFn: (row) => row.startDate,
        cell: ({ row }) => {
          const value = row.original.startDate
          return (
            <Stack alignItems="center" justifyContent="start" direction="row" spacing={1}>
              <CalendarIcon fontSize="small" />
              <Chip
                color="secondary"
                label={
                  <span>
                    {dayjs(value).isSame(dayjs(), 'day')
                      ? t(`moment:calendar:today`)
                      : dayjs(value).format('DD/MM/YYYY')} -{' '}
                    <b>{dayjs(value).format('HH[h]mm')}</b>
                  </span>
                }
                variant="filled"
              />
            </Stack>
          )
        },
      },
      {
        id: 'actions',
        header: t('common:table.header.action'),
        meta: {
          align: 'right',
        },
        cell: ({ row }) => !disableActions && (
          <Stack justifyContent="flex-end" direction="row" spacing={1}>
            <Link to={`/consultations/${row.original.id}`} target="_blank">
              <ChipButton color="info">
                <LaunchIcon />
              </ChipButton>
            </Link>
          </Stack>
        ),
      }
    ]
  }, [t])


  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(defaultPageSize)

  const onPaginationChange = useCallback(({ pageIndex, pageSize }: PaginationState) => {
    setPage(pageIndex)
    setPageSize(pageSize)
  }, [setPage, setPageSize])

  useEffect(() => {
    if (fetchConsultations) {
      fetchConsultations({
        patientId: patientId ?? '',
        options: {
          order: 'DESC',
          limit: pageSize,
          skip: page * pageSize
        }
      })
    }
  }, [fetchConsultations, patientId, page, pageSize])



  return (
    <>
      {!isMobile ? (
        <Table
          columns={columns}
          data={data}
          manualPagination
          onPaginationChange={onPaginationChange}
          totalRows={totalItems}
          rowsPerPageOptions={pageSizeOptions}
        />
      ) : (
        <ConsultationLightTableMobile
          data={data}
          page={page}
          totalItems={totalItems}
          defaultPageSize={defaultPageSize}
          pageSizeOptions={pageSizeOptions}
          onPaginationChange={onPaginationChange}
          onAcceptConsultation={onAcceptConsultation}
          onRefuseConsultation={onRefuseConsultation}
        />
      )}
    </>
  )
}

export default ConsultationLightTable